import { insert, query, remove, run, update } from '../db'
import { uid } from '../lib/id'
import { round2 } from '../lib/money'
import { today as todayISO } from '../lib/dates'
import { placeholders } from '../lib/ledger-sql'
import { dataChanged } from './live'
import { categoryIdFor } from './categories'

/**
 * A transaction stores its category's id; the name is joined back in, because
 * the name is what the whole app reads (`tx.category`). Write `category_id`.
 * `ownTags` are the tags put on the row itself — its category's come on top,
 * through `tagIdsOn` in stores/index.js.
 */
const COLUMNS = `t.*, COALESCE(c.name, '') AS category,
  (SELECT group_concat(tag_id) FROM transaction_tags WHERE transaction_id = t.id) AS own_tags`
const FROM = `FROM transactions t LEFT JOIN categories c ON c.id = t.category_id`

const read = (row) => {
  const { own_tags: tags, ...rest } = row
  return { ...rest, ownTags: tags ? String(tags).split(',') : [] }
}

/**
 * The ledger changed underneath: everything the database works out is asked
 * again. Nothing is loaded here any more — the list reads its own pages, and
 * everything else arrives already added up (stores/live.js).
 */
export async function loadTransactions() {
  dataChanged()
}

/** These transactions, whole, in no particular order. */
export async function transactionsByIds(ids) {
  const list = [...new Set(ids ?? [])]
  const out = []
  // In slices: a statement has a limit on how many parameters it takes.
  for (let i = 0; i < list.length; i += 500) {
    const slice = list.slice(i, i + 500)
    out.push(...(await query(`SELECT ${COLUMNS} ${FROM} WHERE t.id IN (${placeholders(slice)})`, slice)))
  }
  return out.map(read)
}

export const transactionById = async (id) => (id ? ((await transactionsByIds([id]))[0] ?? null) : null)

/**
 * Every transaction, newest first — for the few things that really do have to
 * look at all of them: training the suggester, hunting duplicates. Asked for
 * when they are, never kept.
 */
export async function allTransactions({ columns = COLUMNS } = {}) {
  return (await query(`SELECT ${columns} ${FROM} ORDER BY t.date DESC, t.created_at DESC`)).map(read)
}

/** The transactions of a stretch of days, optionally only these categories (by id). */
export async function transactionsBetween(from, to, { categoryIds } = {}) {
  const params = [from, to]
  let only = ''
  if (categoryIds) {
    only = ` AND t.category_id IN (${placeholders(categoryIds)})`
    params.push(...categoryIds)
  }
  return (
    await query(`SELECT ${COLUMNS} ${FROM} WHERE t.date BETWEEN ? AND ?${only} ORDER BY t.date, t.created_at`, params)
  ).map(read)
}

function normalise(input) {
  const kind = input.kind ?? 'expense'
  let amount = round2(Number(input.amount) || 0)
  // Sign is derived from the kind, so the form only ever asks for a magnitude.
  // The kind itself is not written: the database works it out from the sign.
  amount = kind === 'income' ? Math.abs(amount) : -Math.abs(amount)
  return {
    date: input.date || todayISO(),
    description: (input.description ?? '').trim(),
    amount,
    notes: (input.notes ?? '').trim(),
    payment_type: (input.payment_type ?? '').trim(),
  }
}

export async function addTransaction(input) {
  const now = new Date().toISOString()
  const row = { id: uid('tx'), ...normalise(input), created_at: now, updated_at: now }
  row.category_id = await categoryIdFor(input.category)
  await insert('transactions', row)
  dataChanged()
  return row
}

export async function updateTransaction(id, input) {
  const row = normalise(input)
  row.category_id = await categoryIdFor(input.category)
  await update('transactions', id, { ...row, updated_at: new Date().toISOString() })
  dataChanged()
}

export async function deleteTransaction(id) {
  // Not left to ON DELETE CASCADE: the Capacitor driver does not turn foreign keys on.
  await run('DELETE FROM attachments WHERE transaction_id = ?', [id])
  await remove('transactions', id)
  dataChanged()
  // Its attachments' rows went with it (the foreign key); their bytes go now.
  import('./attachments').then((m) => m.sweep()).catch(() => {})
}
